import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FiltersSidebar, Metric, Page, PageStatus } from "src/components";
import { useLoaders } from "src/components/InlineLoader";
import {
  Button,
  Checkbox,
  Color,
  Flex,
  Grid,
  HorizontalDivider,
  Icon,
  Scroller,
  Select,
  SubgridTable,
  Text,
} from "src/elements";
import styled from "styled-components";

import { useApi } from "src/api";
import { ViewAsStoreTemplate } from "src/components/ViewAsStoreTemplate";
import { pages } from "src/utils";
import { useNavigation } from "src/utils/navigation";
import { sleep } from "src/utils/sleep";
import { MerchflowHeader } from "../../components/merchflows/MerchflowHeader/MerchflowHeader";
import {
  PogOverview,
  pogOverviewHeight,
} from "../Merchflows_merchflowId_Stores_storeCode/components/PogOverview";

import {
  ResponseGetMerchflows_merchflowId_Templates,
  ResponseGetMerchflows_merchflowId_TemplatesList,
  RouteGetMerchflows_merchflowId_Templates,
  RouteGetMerchflows_merchflowId_TemplatesList,
} from "@CommonApi/merchflows/_merchflowId_/templates";
import { RoutePutBasePogSelectedStatus } from "@CommonApi/pogger/base-pog/status";
import { routeToUrl } from "@CommonUtils/routes";
import { useFiltersSidebar } from "src/components/FiltersSidebar/store/hooks";
import { SubgridColumn } from "src/elements/SubgridTable/store/types";
import { useQueryState } from "src/hooks/queryState";
import { useSearchParams } from "src/utils/useSearchParams";
import { merchflowTemplatesFilters } from "./store/filters";
import { RoutePostMerchflows_merchflowId_ExportsBatch } from "@CommonApi/merchflows/_merchflowId_/exports/batch";

const Layout = styled(Grid)<{ isVertical?: boolean }>`
  width: 100%;
  padding: 20px;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  grid-auto-rows: ${pogOverviewHeight};
  gap: 20px;
`;

const shouldFinishLoad = (
  data:
    | ResponseGetMerchflows_merchflowId_Templates
    | ResponseGetMerchflows_merchflowId_TemplatesList
    | undefined
    | null,
) => {
  if (!data || !data.length) return false;
  return !data.some((template) =>
    ["IN_PROGRESS", "NOT_STARTED"].includes(template.base_pog.base_pog_status),
  );
};

export const getTemplateAllListTableColumns = ({
  merchflowId,
  selection,
  setSelection,
  isAllSelected,
  setIsAllSelected,
}: {
  merchflowId: number;
  selection: number[];
  setSelection: (selection: number[]) => void;
  isAllSelected: boolean;
  setIsAllSelected: (isAllSelected: boolean) => void;
}) => {
  return [
    {
      id: "selection",
      header: "selection",
      justify: "center",
      width: "min-content",
      headerRenderer: (
        <Checkbox width="min-content" isChecked={isAllSelected} setIsChecked={setIsAllSelected} />
      ),
      renderer: ({ row: { base_pog } }) => (
        <Checkbox
          width="min-content"
          isChecked={
            base_pog !== undefined
              ? isAllSelected
                ? selection.includes(base_pog.base_pog_id)
                  ? false
                  : true
                : selection.includes(base_pog.base_pog_id)
              : false
          }
          setIsChecked={() => {
            if (base_pog !== undefined) {
              if (selection.includes(base_pog.base_pog_id)) {
                setSelection(selection.filter((s) => s !== base_pog.base_pog_id));
              } else {
                setSelection([...selection, base_pog.base_pog_id]);
              }
            }
          }}
          isDisabled={base_pog === undefined}
        />
      ),
    },
    {
      id: "basePogId",
      header: "Base Pog ID",
      justify: "center",
      renderer: ({ row }) => (
        <Link
          to={`${routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
            merchflowId,
            basePogId: row.base_pog.base_pog_id,
          })}`}
          style={{ textDecoration: "none" }}
        >
          <Text variant="small2">{row.base_pog.base_pog_id}</Text>
        </Link>
      ),
    },
    {
      id: "templateId",
      header: "Template ID",
      justify: "center",
      renderer: ({ row }) => row.template_id,
    },
    {
      id: "status",
      header: "Status",
      justify: "center",
      renderer: ({ row }) => {
        switch (row.base_pog.base_pog_status) {
          case "NOT_STARTED":
          case "IN_PROGRESS":
            return <Icon name="spinner" size={14} />;
          case "DONE":
            return <Icon name="checkmarkCircle" size={14} />;
          case "ERROR":
            return <Icon name="warningCircle" size={14} color={Color.red} />;
        }
      },
    },

    {
      id: "returnOnSpace",
      header: "Return on Space",
      width: "120px",
      justify: "center",
      renderer: ({ row }) =>
        row.base_pog.base_pog_data && (
          <Metric percent={row.base_pog.base_pog_data.metrics?.returnOnSpace} isInline />
        ),
    },
    {
      id: "coreRange",
      header: "Core Range",
      width: "120px",
      justify: "center",
      renderer: ({ row }) =>
        row.base_pog.base_pog_data && (
          <Metric percent={row.base_pog.base_pog_data.metrics?.coreRange} isInline />
        ),
    },
    {
      id: "shelfAlignment",
      header: "Shelf Alignment",
      width: "120px",
      justify: "center",
      renderer: ({ row }) =>
        row.base_pog.base_pog_data && (
          <Metric percent={row.base_pog.base_pog_data.metrics?.shelfAlignment} isInline />
        ),
    },
    {
      id: "dosMos",
      header: "DOS/MOS",
      width: "120px",
      justify: "center",
      renderer: ({ row }) =>
        row.base_pog.base_pog_data && (
          <Metric percent={row.base_pog.base_pog_data.metrics?.dosMos} isInline />
        ),
    },
  ] satisfies SubgridColumn<ResponseGetMerchflows_merchflowId_TemplatesList[number]>[];
};

export const PageMerchflowTemplates = () => {
  const { merchflowId: _merchflowId } = useParams<{
    merchflowId: string;
  }>();
  const merchflowId = Number(_merchflowId);

  const { postExportBatchPogsApi, getTemplatesApi, getTemplatesListApi, putBasePogStatusApi } =
    useApi();
  const { hasLoader } = useLoaders();
  const { navigate } = useNavigation();
  const { openFilters } = useFiltersSidebar();

  const [gridData, setGridData] = useState<ResponseGetMerchflows_merchflowId_Templates | null>(
    null,
  );
  const [listData, setListData] = useState<ResponseGetMerchflows_merchflowId_TemplatesList | null>(
    null,
  );
  const [filteredData, setFilteredData] = useState(gridData);
  const [viewMode, _setViewMode] = useQueryState<"list" | "grid">("view", "list");
  const { appendSearchParams } = useSearchParams();

  const setViewMode = (view: "list" | "grid") => {
    if (view === "grid") {
      setListData(null);
    } else {
      setGridData(null);
    }
    _setViewMode(view);
    appendSearchParams({
      view,
    });
  };

  const [selection, setSelection] = useState<number[]>([]);
  const [isAllSelected, _setIsAllSelected] = useState(false);
  const [isExportingDone, setIsExportingDone] = useState(false);
  const [isFavoriteDone, setIsFavoriteDone] = useState(false);
  const [initLoading, setIsInitLoading] = useState(true);

  const setIsAllSelected = (isAllSelected: boolean) => {
    _setIsAllSelected(isAllSelected);
    setSelection([]);
  };

  const isLoading =
    initLoading ||
    hasLoader(RouteGetMerchflows_merchflowId_Templates) ||
    hasLoader(RouteGetMerchflows_merchflowId_TemplatesList);
  const isFavoriteLoading = hasLoader(RoutePutBasePogSelectedStatus);
  const isExportLoading = hasLoader(RoutePostMerchflows_merchflowId_ExportsBatch);

  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    const loadData = () => {
      if (viewMode === "grid") {
        setListData(null);
        loadTemplates(interval);
      } else {
        setGridData(null);
        loadTemplatesList(interval);
      }
      if (initLoading) {
        setIsInitLoading(false);
      }
    };

    interval = setInterval(() => {
      loadData();
    }, 10000);

    loadData();

    return () => {
      if (interval) {
        clearInterval(interval);
        interval = undefined;
      }
    };
  }, [viewMode]);

  const loadTemplates = async (interval?: NodeJS.Timeout) => {
    const response = await getTemplatesApi(merchflowId);
    setGridData(response);

    if (shouldFinishLoad(response)) {
      if (interval) {
        clearInterval(interval);
      }
    }
  };

  const loadTemplatesList = async (interval?: NodeJS.Timeout) => {
    const response = await getTemplatesListApi(merchflowId);
    setListData(response);

    if (shouldFinishLoad(response)) {
      if (interval) {
        clearInterval(interval);
      }
      return;
    }
  };

  const onClickTemplate = (basePogId: number) => {
    navigate(
      routeToUrl(pages.merchflows_merchflowId_BasePog_basePogId_, {
        merchflowId,
        basePogId,
      }),
    );
  };

  const onClickFavorite = async () => {
    const response = await putBasePogStatusApi({
      is_favourite: selection,
    });

    if (response) {
      loadTemplates();
      setIsAllSelected(false);
      setSelection([]);
      setIsFavoriteDone(true);
      setTimeout(() => {
        setIsFavoriteDone(false);
      }, 1000);
    }
  };

  const onClickUnFavorite = async () => {
    const response = await putBasePogStatusApi({
      is_not_favourite: selection,
    });

    if (response) {
      loadTemplates();
      setIsAllSelected(false);
      setSelection([]);
      setIsFavoriteDone(true);
      setTimeout(() => {
        setIsFavoriteDone(false);
      }, 1000);
    }
  };

  const exportSelectedStorePogs = async () => {
    const basePogIds = isAllSelected
      ? (gridData || listData)
          ?.filter((template) => selection.includes(template.base_pog.base_pog_id))
          .map((template) => template.base_pog.base_pog_id)
      : selection;

    const response = await postExportBatchPogsApi(merchflowId, {
      type: "psaV2",
      base_pog_ids: basePogIds || [],
    });

    if (response?.status === "DONE") {
      setIsExportingDone(true);
      setIsAllSelected(false);
      setSelection([]);
      setIsAllSelected(false);
      sleep(1000);
      setIsExportingDone(false);
    }
  };

  return (
    <Page>
      <MerchflowHeader merchflowId={merchflowId} />
      <Flex margin="10px 0 0 0">
        <Button
          color="greenSmoke"
          iconRight={{ name: "filter" }}
          isDisabled={isLoading}
          onClick={openFilters}
        >
          Filters
        </Button>
      </Flex>
      <Flex minHeight="0" borderColor={Color.lightGrayHover} borderRadius="3px" column flexGrow>
        <Flex gap="10px" justify="between" padding="15px 10px 10px 10px">
          <ViewAsStoreTemplate merchflowId={merchflowId} isTemplate />

          <Flex gap="10px">
            <Button
              isSuccessful={isFavoriteDone}
              isLoading={isFavoriteLoading}
              dropdown={[
                {
                  label: "Favorite selected",
                  onClick: onClickFavorite,
                  isDisabled: selection.length === 0,
                },
                {
                  label: "Un-favorite selected",
                  onClick: onClickUnFavorite,
                  isDisabled: selection.length === 0,
                },
              ]}
            >
              Favorite
            </Button>

            <Button
              isLoading={isExportLoading}
              isSuccessful={isExportingDone}
              dropdown={[
                {
                  label: "Export selected",
                  onClick: exportSelectedStorePogs,
                  isDisabled:
                    selection.length === 0 ||
                    (isAllSelected && selection.length === (gridData || listData)?.length),
                },
              ]}
            >
              Exports
            </Button>
            <Select
              width="100px"
              title="Show as"
              isDisabled={gridData === undefined && listData === undefined}
              value={viewMode}
              setValue={setViewMode}
              options={[
                {
                  label: "Grid",
                  value: "grid",
                },
                {
                  label: "List",
                  value: "list",
                },
              ]}
            />
          </Flex>
        </Flex>

        <HorizontalDivider color={Color.lightGrayHover} />

        <Flex width="100%" minHeight="0" flexGrow>
          {listData && viewMode === "list" && (
            <Flex flexGrow column gap="5px" data-testid="store-pogs-list">
              <SubgridTable
                page={1}
                columns={getTemplateAllListTableColumns({
                  merchflowId,
                  selection,
                  setSelection,
                  isAllSelected,
                  setIsAllSelected,
                })}
                data={listData}
              />
            </Flex>
          )}

          {!isLoading && gridData && viewMode === "grid" && (
            <Scroller>
              <Layout>
                {filteredData?.map((template, i) => (
                  <Flex
                    key={i}
                    width="100%"
                    onClick={() => onClickTemplate(template.base_pog.base_pog_id)}
                  >
                    <PogOverview
                      key={i}
                      selection={selection}
                      label={`TemplateID: ${template.template_id}`}
                      badge={template.total_base_pog > 1 ? template.total_base_pog : undefined}
                      pog={template.base_pog.base_pog_data ?? undefined}
                      thumbnail={template.base_pog.thumbnail}
                      status={
                        (template.total_favourite_base_pog > 1 && "ERROR") ||
                        (template.base_pog.base_pog_status === "ERROR" && "ERROR") ||
                        ((template.base_pog.base_pog_status === "NOT_STARTED" ||
                          template.base_pog.base_pog_status === "IN_PROGRESS") &&
                          "IN_PROGRESS") ||
                        undefined
                      }
                      selectionId={
                        template.total_base_pog === 1 ? template.base_pog.base_pog_id : undefined
                      }
                      setSelection={setSelection}
                      width="100%"
                      height="100%"
                    />
                  </Flex>
                ))}
              </Layout>
            </Scroller>
          )}
          {!gridData && !listData && !isLoading && (
            <PageStatus label="No base pog generated" icon="spinner" />
          )}
        </Flex>
      </Flex>

      <FiltersSidebar
        filters={merchflowTemplatesFilters}
        data={gridData || null}
        setFilteredData={setFilteredData}
      />
    </Page>
  );
};
